import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useApiConfig } from "../lib/api";
import { addrUrl } from "../lib/config";
import { useMode } from "../lib/demo";
import "./portfolio.css";
import "./docs.css";

const SECTIONS = [
  ["basics", "The basics"],
  ["buy", "Buy a call or a put"],
  ["cashout", "Cash out"],
  ["earn", "Sell options"],
  ["rewards", "Holder rewards"],
  ["fees", "Fees"],
  ["safety", "Safety"],
  ["risks", "Risks"],
  ["contracts", "Contracts"],
] as const;

const WORDS: [string, string, string][] = [
  ["Call", "call", "The right to buy $STRIKE at a set market cap. Wins when the price goes up."],
  ["Put", "put", "The right to sell $STRIKE at a set market cap. Wins when the price goes down."],
  ["Strike", "", "The market cap your option is about. A $5M call pays if $STRIKE ends above $5M."],
  ["Expiry", "", "When the option ends, always at 20:00 UTC: daily, weekly (Fridays) and monthly. You can cash out any time before."],
  ["Premium", "", "What you pay for the option, in ETH. It is also the most you can lose."],
  ["In the money", "", "When your option is worth something now: above the strike for calls, below for puts."],
];

function Sec({ id, n, title, children }: { id: string; n: number; title: string; children: ReactNode }) {
  return (
    <section id={id} className="doc-sec">
      <h2>
        <span className="doc-n num">{String(n).padStart(2, "0")}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

export function Docs() {
  const { data: cfg } = useApiConfig();
  const live = useMode() === "live";
  const c = live ? cfg?.contracts : undefined;
  const rows: [string, string | null | undefined, string][] = [
    ["$STRIKE token", c?.token, "The coin, launched on pons."],
    ["StrikeOptions", c?.options, "Holds all collateral. Cannot be upgraded."],
    ["StrikeExchange", c?.exchange, "Settles orders and takes the 10% fee."],
    ["HouseVault", c?.vault, "The protocol's own option seller."],
    ["StrikeRouter", c?.router, "Pay in ETH, one-click cash-out, auto-exercise."],
    ["StrikeTreasury", c?.treasury, "Receives fees and pays them to the distributor."],
    ["RewardsDistributor", c?.distributor, "Holder payouts, with a 2-hour safety window."],
  ];

  return (
    <section className="wrap page docs">
      <aside className="docs-nav">
        <span className="eyebrow">On this page</span>
        {SECTIONS.map(([id, label]) => (
          <a key={id} href={`#${id}`}>
            {label}
          </a>
        ))}
      </aside>

      <article className="docs-body">
        <header className="page-head">
          <span className="eyebrow">Docs</span>
          <h1>How $STRIKE works.</h1>
          <p>
            $STRIKE is a coin on Robinhood Chain with its own market for calls and puts. Hold it to earn ETH, or trade options on where
            its price goes next. Five minutes of reading covers everything.
          </p>
        </header>

        <Sec id="basics" n={1} title="The basics">
          <div className="glossary">
            {WORDS.map(([w, tone, d]) => (
              <div key={w} className="gl">
                <span className={`gl-w ${tone}`}>{w}</span>
                <p>{d}</p>
              </div>
            ))}
          </div>
          <p>One option covers one $STRIKE, and prices are shown per 1M options. Strikes are written as market caps so you never have to think in tiny token prices.</p>
        </Sec>

        <Sec id="buy" n={2} title="Buy a call or a put">
          <ol className="steps">
            <li>
              Open <Link to="/trade">Trade</Link> and pick an expiry. Calls are on the left, puts on the right.
            </li>
            <li>Tap a strike. The ticket shows the price, your breakeven and what you make if it moves.</li>
            <li>Choose how many and press Buy. You pay in ETH and the options land in your wallet.</li>
          </ol>
          <p className="callout">
            Options are ERC-1155 tokens. Normal wallets hold them fine. If a smart-contract wallet cannot receive them, the purchase just
            fails and nothing is spent.
          </p>
        </Sec>

        <Sec id="cashout" n={3} title="Cash out">
          <p>
            When your option is in the money, press <strong>Cash out</strong> in <Link to="/portfolio">Portfolio</Link>. One transaction
            uses the option, sells the result in the $STRIKE pool and sends you the profit in ETH.
          </p>
          <p>
            Turn on <strong>auto-exercise</strong> and we cash out for you in the last hour before expiry, only if it leaves you a
            profit. Anything not used by expiry ends worthless.
          </p>
        </Sec>

        <Sec id="earn" n={4} title="Sell options">
          <p>
            On <Link to="/earn">Earn</Link> you can be the seller. A <strong>covered call</strong> locks $STRIKE you own; a{" "}
            <strong>cash-secured put</strong> locks ETH. You set the price, and your collateral is only locked when someone buys. The
            premium is yours either way, minus the 10% fee.
          </p>
        </Sec>

        <Sec id="rewards" n={5} title="Holder rewards">
          <p>
            Twice a day (00:00 and 12:00 UTC) 70% of the fees collected are paid to holders in ETH, by <strong>average balance</strong> over the
            last 12 hours. Claim on <Link to="/rewards">Rewards</Link> any time within 60 days. Wallets averaging under $10 are skipped,
            and $STRIKE locked in covered calls still counts. The other 30% funds the options vault, so there are always calls and puts to buy.
          </p>
        </Sec>

        <Sec id="fees" n={6} title="Fees">
          <table className="docs-table">
            <tbody>
              <tr>
                <td>Creator tax on every $STRIKE trade on pons</td>
                <td className="call">70% holders · 30% options vault</td>
              </tr>
              <tr>
                <td>10% of every option premium</td>
                <td className="call">70% holders · 30% options vault</td>
              </tr>
              <tr>
                <td>The other 90% of the premium</td>
                <td>to the seller</td>
              </tr>
            </tbody>
          </table>
        </Sec>

        <Sec id="safety" n={7} title="Safety">
          <ul className="checks">
            <li>
              <strong>Fully backed.</strong> Every call is backed by $STRIKE and every put by ETH, locked in a contract that cannot be upgraded.
            </li>
            <li>
              <strong>Nothing to manipulate.</strong> No price feed decides who wins. Cashing out is a real trade, so pumping the pool
              costs more than it pays.
            </li>
            <li>
              <strong>Guarded quotes.</strong> A quote fails on-chain if the price moved more than 3% since it was made.
            </li>
          </ul>
        </Sec>

        <Sec id="risks" n={8} title="Risks">
          <p>
            Options can expire worthless and memecoins can go to zero. Large cash-outs in a thin pool lose more to slippage. The contracts
            have not had an external audit yet. Nothing here is investment advice. Not available to persons in the United States or other
            restricted jurisdictions.
          </p>
        </Sec>

        <Sec id="contracts" n={9} title="Contracts">
          {!live && <p className="note" style={{ marginBottom: 8 }}>Addresses are published here at launch.</p>}
          <table className="docs-table">
            <tbody>
              {rows.map(([name, a, desc]) => (
                <tr key={name}>
                  <td>
                    <strong>{name}</strong>
                    <div className="note">{desc}</div>
                  </td>
                  <td className="mono">
                    {a ? (
                      <a href={addrUrl(a)} target="_blank" rel="noreferrer">
                        {a}
                      </a>
                    ) : (
                      <span className="faint">at launch</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Sec>
      </article>
    </section>
  );
}
